/**
 * Which advisory-partner profile applies right now. Two answers, kept apart.
 *
 * **Brand** is the host's alone. `GET /advisory/current/` maps the request's Host
 * header through django.contrib.sites to an `AdvisoryPartner`: a white-label subdomain
 * answers with that firm, and the main app (`app.valur.com`) answers with its own
 * "house" row, named Valur. Logo, colours, font, the partner's copy and its tenant
 * settings all follow that answer, so the main app never shows a partner's brand —
 * not to its clients, and not to an advisor or a staff member who belongs to one.
 *
 * **Workspace** is the firm the advisor pages (clients, members, settings) act on, and
 * it follows the host too: the tenant on a partner subdomain, Valur's own workspace on
 * the main app. Superusers run whichever one they are on (the backend's
 * acts_as_workspace_admin), so invitations and assignments land where they were made.
 * The one exception is an advisor whose firm has no live subdomain (self-registered
 * workspaces are reserve-don't-serve, see docs/features/advisor-self-registration.md):
 * they have nowhere else to work, so on the main app their workspace falls back to the
 * *membership* — the `marketing/getOwnWorkspaceProfile` action. That fallback never
 * reaches the brand.
 *
 * Kept free of imports: `services/theme.js` depends on it and is unit-tested by
 * importing the module directly, outside the app's module-alias/runtime setup.
 */

// The main app's own AdvisoryPartner row is named "Valur". Matching on the name is
// the convention the rest of the app already uses to mean "not a white-label
// tenant" (see `isPartner` in mixins/advisory_partner_mixin.js).
export const HOUSE_PARTNER_NAME = 'Valur';

/** True for the main app's own partner row — present, but not a white-label tenant. */
export function isHousePartner(profile) {
    return (profile?.name || '') === HOUSE_PARTNER_NAME;
}

/** True when `profile` is a real white-label tenant (populated and not the house row). */
export function isTenantPartner(profile) {
    return Object.keys(profile || {}).length > 0 && !isHousePartner(profile);
}

/**
 * Combine the host answer with the caller's own-workspace answer into the *workspace*
 * the advisor pages act on. Not a branding input: branding is the host's alone.
 *
 * Both inputs are tri-state, and both distinctions matter:
 * a profile object is an answer, `null` means "definitively nothing here" (404/403),
 * and `undefined` means the lookup failed inconclusively (network or 5xx).
 *
 * The result carries the same tri-state for the caller to act on: a profile to work
 * in, `null` to clear the workspace, or `undefined` to change nothing at all.
 *
 * The `undefined` case is the one worth being careful about. Treating a transient
 * failure as "no workspace" would swap an advisor's firm for the house record
 * mid-session and point their next workspace write at Valur's uuid, which 403s. A
 * failed lookup must leave the applied workspace alone.
 */
export function mergeWorkspaceProfile(hostProfile, ownProfile) {
    if (ownProfile === undefined) return undefined;
    // `null` = the caller has no workspace, so the host's own answer stands (on the
    // main app that's the house record, which is exactly right for a non-advisor).
    return ownProfile || hostProfile;
}
